import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  createStyles,
  Grid,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core';
import React from 'react';
import { useTranslation } from '../../../../hooks/useTranslation';
import colors from '../../../../themes/main-colors';
import { ExpandMoreOutlined } from '@material-ui/icons';
import clsx from 'clsx';
import SSNTextField from '../../../ssn-text-field';

import { IDENTITY_VERIFICATION_PANEL_TYPES } from './';
import { BaseFormModel } from '../../../../hooks/useFormState.types';
import { PersonalIdentificationType } from '../../../../__generated__/pge-types';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
      '& .MuiOutlinedInput-root': {
        background: theme.palette.common.white,
      },
    },
    header: {
      fontWeight: 'bold',
      fontSize: '20px',
    },
    panelOpened: {
      background: colors.aliceBlue,
    },
    panelHeader: {
      fontSize: '1rem',
      color: colors.sparkBlue,
      '&.active': {
        color: colors.black,
        fontWeight: theme.typography.fontWeightBold,
      },
    },
    panelDetailsContainer: {
      padding: '0px 16px 16px',
    },
    panelDetails: {
      padding: theme.spacing(0, 2, 2, 2),
    },
    panelSummary: {
      minHeight: '50px !important',
      paddingTop: 10,
      '& .MuiAccordionSummary-content.Mui-expanded': {
        margin: '4px 0',
      },
    },
  }),
);

interface Props {
  form: BaseFormModel;
  activePanel: IDENTITY_VERIFICATION_PANEL_TYPES;
  setActivePanel: (e: IDENTITY_VERIFICATION_PANEL_TYPES) => void;
}

const SSNVerification = ({ form, activePanel, setActivePanel }: Props) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const SSN_PANEL = IDENTITY_VERIFICATION_PANEL_TYPES.SSN;

  return (
    <Accordion
      expanded={activePanel === SSN_PANEL}
      onChange={async () => {
        if (activePanel === SSN_PANEL) {
          return false;
        }

        setActivePanel(SSN_PANEL);

        await Promise.all([
          form.setValue('idState', ''),
          form.setValue('idValue', ''),
          form.setValue('idType', PersonalIdentificationType.Ssn),
        ]);

        form.setError('idType', null);
        form.setError('idState', null);
        form.setError('idValue', null);
      }}
      className={clsx({
        [classes.panelOpened]: activePanel === SSN_PANEL,
      })}
    >
      <AccordionSummary
        expandIcon={activePanel !== SSN_PANEL && <ExpandMoreOutlined />}
        aria-controls="panel1bh-content"
        id="panel1bh-header"
        className={classes.panelSummary}
      >
        <Typography
          className={clsx({
            [classes.panelHeader]: true,
            active: activePanel === 'ssn',
          })}
          data-testid="ssn-summary-panel"
        >
          {t('SOCIAL_SECURITY_NUMBER')}
        </Typography>
      </AccordionSummary>

      <AccordionDetails
        data-testid="ssn-accordion-details"
        className={classes.panelDetailsContainer}
      >
        <Grid container item className={classes.panelDetails}>
          <Grid item xs={12} md={12}>
            {activePanel === SSN_PANEL && (
              <SSNTextField
                name="idValue"
                label={t('SOCIAL_SECURITY_NUMBER')}
                style={{
                  width: '100%',
                  background: 'transparent',
                }}
                className={classes.root}
                {...form.props('idValue')}
                error={Boolean(form.errors.idValue)}
              />
            )}
          </Grid>
          <Grid item xs={12} md={12}>
            <Typography variant="subtitle1">
              {t('SSM_VERIFY_CUST_SSN_DISCLAIMER')}
            </Typography>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

export default SSNVerification;
